import { addMinutes } from 'date-fns';
import { formatDate, formatDateRange, isoDateStringToDate } from 'lib/riskbase';
import { EngageFlatScheduledVisit } from 'types/Engage';
import { API_DATE_FORMAT } from 'lib/constants';
import { ISODateString } from 'types/types';

export type ScheduledVisitSlot = EngageFlatScheduledVisit['slots'][number];

export type ScheduledVisitSlotDayGroup = {
  key: string;
  slotDate: Date;
  slots: ScheduledVisitSlot[];
};

export const getScheduledVisitDateRangeText = (
  visit: EngageFlatScheduledVisit,
): string => {
  if (visit.startDateTime && visit.endDateTime) {
    return formatDateRange(
      isoDateStringToDate(visit.startDateTime),
      addMinutes(isoDateStringToDate(visit.endDateTime), 1),
      'long',
    );
  }

  const slotDates = visit.slots
    .map((slot) => isoDateStringToDate(slot.startDateTime))
    .sort((a, b) => a.getTime() - b.getTime());
  const [firstDate] = slotDates;
  const lastDate = slotDates[slotDates.length - 1];

  if (!firstDate || !lastDate) {
    return 'No Dates';
  }

  return formatDateRange(firstDate, lastDate, 'long');
};

export const getScheduledVisitSlotDayGroups = (
  slots: ScheduledVisitSlot[],
): ScheduledVisitSlotDayGroup[] =>
  Object.values(
    [...slots]
      .sort(
        (slotA, slotB) =>
          isoDateStringToDate(slotA.startDateTime).getTime() -
          isoDateStringToDate(slotB.startDateTime).getTime(),
      )
      // Group slots by day
      .reduce<Record<string, ScheduledVisitSlotDayGroup>>((acc, slot) => {
        const slotDate = isoDateStringToDate(slot.startDateTime);
        const dateKey = formatDate(slotDate, API_DATE_FORMAT);
        // Create the day group if it doesn't exist
        // (first slot for that day)
        if (!acc[dateKey]) {
          acc[dateKey] = {
            key: dateKey,
            slotDate,
            slots: [],
          };
        }

        acc[dateKey].slots.push(slot);
        return acc;
      }, {}),
  );

export const formatScheduledVisitSlotTimeRange = (
  startDateTime: ISODateString,
  endDateTime: ISODateString,
): string => {
  // "startDateTime": "2026-04-15T10:00:00.000Z",
  // "endDateTime": "2026-04-15T11:59:59.000Z"
  // TO:
  // "startTimeText": "11am",
  // "endTimeText": "1pm"
  const startTimeText = formatDate(
    isoDateStringToDate(startDateTime),
    'h:mma',
  ).toLowerCase();
  const endTimeText = formatDate(
    addMinutes(isoDateStringToDate(endDateTime), 1),
    'h:mma',
  ).toLowerCase();

  // "startTimeText": "5pm",
  // "endTimeText": "6pm"
  // TO:
  // "5-6pm"
  const startMeridiem = startTimeText.endsWith('am') ? 'am' : 'pm';
  const endMeridiem = endTimeText.endsWith('am') ? 'am' : 'pm';
  if (startMeridiem && startMeridiem === endMeridiem) {
    return `${startTimeText.slice(0, -2)} – ${endTimeText}`;
  }
  return `${startTimeText} – ${endTimeText}`;
};
